import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { SgTextVariant } from '../sg-text/sg-text.component';
import { isStorybookEnvironment } from '../storybook-utils';
import { SgButtonVariant } from '../../..';
export type EmptyStateVariants = 'empty' | 'error' | 'firstTime' | 'completed' | 'comingSoon' | 'loading';
export type EmptyStateSize = 'small' | 'medium' | 'large';

@Component({
  selector: 'sg-empty-state',
  templateUrl: './empty-state.component.html',
  styleUrls: ['./empty-state.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      state('in', style({ opacity: 1 })),
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 1 })),
      ]),
    ]),
  ],
  standalone: false,
  // `title` is an input rendered as the heading; drop the native attribute so no browser tooltip shows on hover.
  host: { '[attr.title]': 'null' },
})
export class SgEmptyStateComponent {
  @Input() variant: EmptyStateVariants = 'firstTime';
  @Input() title?: string;
  @Input() message?: string | undefined;
  @Input() size: EmptyStateSize = 'medium';
  @Input() submitButtonText?: string;
  @Input() submitCancelButtonText?: string;
  @Input() submitButtonIcon?: string;
  @Input() cancelButtonIcon?: string;
  @Input() submitButtonVariant: SgButtonVariant = 'primary';
  @Input() cancelButtonVariant: SgButtonVariant = 'secondary';
  /** When true (firstTime variant only), shows the blue illustration instead of the default grayscale one. */
  @Input() secondaryImage = false;
  /** Disables the submit button (still focusable, aria-disabled, so its reason is announced); it never emits `submit`. */
  @Input() submitButtonDisabled = false;
  /** Why the submit button is disabled: shown on hover and read by screen readers via aria-describedby. */
  @Input() submitButtonTooltip?: string;

  @Output() submit = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  // Each variant maps to [primary] or [primary, secondary] image paths.
  // `secondaryImage` selects the second entry when one exists.
  variantToImage: Record<EmptyStateVariants, [string, string?]> = {
    error: ['images/general/empty-state/error.svg'],
    empty: ['images/general/empty-state/empty.svg'],
    completed: ['images/general/empty-state/completed.svg'],
    firstTime: ['images/general/empty-state/first-time-a.svg', 'images/general/empty-state/first-time-a-secondary.svg'],
    comingSoon: ['images/general/empty-state/coming-soon.svg'],
    loading: ['images/general/empty-state/loading.svg'],
  };

  variantToAlt: Record<EmptyStateVariants, string> = {
    error: 'Error',
    empty: 'No results',
    completed: 'Completed',
    firstTime: 'First time',
    comingSoon: 'Coming soon',
    loading: 'Loading',
  };

  variantToTitle: Record<EmptyStateVariants, string> = {
    error: 'Something went wrong',
    empty: 'No Result Found',
    completed: `You're Up to Date`,
    firstTime: 'No Results Yet',
    comingSoon: 'Coming Soon',
    loading: 'Loading data...',
  };

  variantToMessage: Record<EmptyStateVariants, string> = {
    error: `We couldn’t load the data. <br/>Please refresh the page.`,
    empty: `We couldn’t find any results. Try updating your filters or search terms.`,
    completed: ' No issues or errors have been reported',
    firstTime: 'Your results will appear here.',
    comingSoon: 'Stay tuned! This feature is on its way.',
    loading: 'This may take up to 15 seconds',
  };

  sizeToTitleVariant: Record<EmptyStateSize, SgTextVariant> = {
    small: 'sg-body-medium-bold',
    medium: 'sg-heading-medium-bold',
    large: 'sg-heading-large-bold',
  };

  sizeToDescriptionVariant: Record<EmptyStateSize, SgTextVariant> = {
    small: 'sg-body-xsmall-regular',
    medium: 'sg-body-medium-regular',
    large: 'sg-body-large-regular',
  };

  assetsPath: string;

  constructor() {
    this.assetsPath = isStorybookEnvironment()
      ? './' // In Storybook, assets are served from the root
      : '/assets/'; // In the app, use absolute path
  }

  get effectiveImagePath(): string {
    const [primary, secondary] = this.variantToImage[this.variant] ?? ['images/general/empty-state/first-time-a.svg'];
    const relativePath = (this.secondaryImage && secondary) || primary;

    return this.assetsPath + relativePath;
  }

  get effectiveMessageHtml(): string | undefined {
    // If the default or provided message contains any HTML tags, treat it as HTML
    const msg = this.message ?? this.variantToMessage[this.variant];
    return msg && /<[^>]+>/.test(msg) ? msg : undefined;
  }
}
